import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { getAllEmployees } from 'src/services/employeeService';
import { updateCustomer } from 'src/services/projectService';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { AppActions } from 'src/store/AppReducer';
import SearchIcon from '@mui/icons-material/Search';
import { useAppSelector } from 'src/hooks/hooks';

export interface CustomerInformation {
  code: string;
  description: string;
  name: string;
  industry: string;
  region: string;
  engagementLead: any;
  segment?: any;
  status: any;
}

const EditCustomer = ({ 
  toggle, 
  open, 
  data, 
  getData, 
  countries, 
  segments, 
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage, }) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 2,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const selectStyle = {
    width: '94%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };

  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onClose = () => {
    setNameError('');
    setRegionError('');
    setIndustryError('');
    setSegmentError('');
    setStatusError('');
    toggle();
  };

  const { register, handleSubmit, reset, setValue } =
    useForm<CustomerInformation>();
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  const [regionError, setRegionError] = useState('');
  const [industryError, setIndustryError] = useState('');
  const [engagementLeadError, setEngagementLeadError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const [customerObj, setCustomerObj] = useState<any>({
    name: data.name,
    id: data.id,
    description: data.description,
    region: data.country,
    industry: data.industry,
    engagementLead: data.engagementLead,
    engagementLeadId: data.engagementLeadId,
    status: data.isActive ? 'Active' : 'Inactive'
  });

  const toggleConfirmation = () => {
    if (openConfirmation) {
      setOpenConfirmation(false);
    }

    if (segment === '') {
      setSegmentError('Segment is required.');
    } else if (customerObj.name === '') {
      setNameError('Client name required');
    } else if (customerObj.name.trim().length < 1) {
      setNameError('Client name cannot be only spaces');
    } else if (customerObj.region === '') {
      setRegionError('Region required');
    } else if (customerObj.industry === '') {
      setIndustryError('Industry required');
    } else if (customerObj.industry.trim().length < 1) {
      setIndustryError('Industry cannot be only spaces');
    } else if (customerObj.manager === null) {
      setEngagementLeadError('Engagement Lead required');
    } else if (
      (customerObj.name !== '' &&
        customerObj.name.length < 31 &&
        customerObj.name.trim().length > 0 &&
        customerObj.description.length < 51 &&
        customerObj.region !== '' &&
        customerObj.industry !== '' &&
        customerObj.industry.trim().length > 0 &&
        customerObj.engagementLead !== null,
      segment !== '')
    ) {
      setNameError('');
      setRegionError('');
      setIndustryError('');
      setSegmentError('');
      setOpenConfirmation(!openConfirmation);
    }
  };

  const getEmployeeData = () => {
    getAllEmployees().then((res) => {
      setEmployees((res.data as any[]) || []);
    });
  };

  useEffect(() => {
    if (open) {
      getEmployeeData();
    }
  }, [open]);

  useEffect(() => {
    setValue('region', data.country);
    setValue('engagementLead', data.engagementLeadId);
    setValue('segment', data.segmentId);
    setSelectedOption(data.segmentId);
    setCustomerObj({
      name: data.name,
      id: data.id,
      description: data.description,
      region: data.country,
      industry: data.industry,
      engagementLead: data.engagementLead,
      engagementLeadId: data.engagementLeadId,
      status: data.isActive ? 'Active' : 'Inactive'
    });
  }, [data, open, setValue]);

  const onSubmit = () => {
    if (
      (customerObj.name !== '' &&
        customerObj.name.length < 31 &&
        customerObj.name.trim().length > 0 &&
        customerObj.description.length < 51 &&
        customerObj.region !== '' &&
        customerObj.industry !== '' &&
        customerObj.industry.trim().length > 0 &&
        customerObj.engagementLead !== null,
      segment !== '')
    ) {
      dispatch(AppActions.showLoader());
      setNameError('');
      setRegionError('');
      setIndustryError('');
      setSegmentError('');
      const body = {
        name: data.name === customerObj.name ? null : customerObj.name,
        description:
          data.description === customerObj.description
            ? null
            : customerObj.description,
        country: data.region === customerObj.region ? null : customerObj.region,
        industry:
          data.industry === customerObj.industry ? null : customerObj.industry,
          engagementLead:
          data.engagementLead === customerObj.engagementLead
            ? null
            : customerObj.engagementLead,
          engagementLeadId:
          data.engagementLeadId === customerObj.engagementLeadId
            ? null
            : customerObj.engagementLeadId,
        segmentId: data.segmentId,
        customerId: customerObj.id,
        newSegmentId: data.segmentId === segment ? null : segment,
        isActive: customerObj.status === 'Active' ? true : false
      };

      updateCustomer(body)
        .then(() => {
          reset();
          setSnackBarMessage('You have updated the client successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          getData();
          toggleConfirmation();
          toggle();
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to update the client.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          toggleConfirmation();
          toggle();
        });
    } else if (segment === '') {
      setSegmentError('Segment is required.');
    } else if (customerObj.name === '') {
      setNameError('Client name required');
    } else if (customerObj.name.trim().length < 1) {
      setNameError('Client name cannot be only spaces');
    } else if (customerObj.region === '') {
      setRegionError('Region required');
    } else if (customerObj.industry === '') {
      setIndustryError('Industry required');
    } else if (customerObj.industry.trim().length < 1) {
      setIndustryError('Industry cannot be only spaces');
    } else if (customerObj.manager === null) {
      setEngagementLeadError('Engagement Lead required');
    }
  };

  const [segmentError, setSegmentError] = useState('');
  const [segment, setSegment] = useState(data?.segmentId);

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [selectedOption, setSelectedOption] = useState(data?.segmentId);
  const [searchText, setSearchText] = useState('');

  const displayedOptions = useMemo(
    () =>
      segments.length > 0 &&
      segments?.filter((option) => containsText(option.name, searchText)),
    [searchText, segments]
  );

  const displayedCountries = useMemo(
    () =>
    countries.length > 0 &&
    countries?.filter((option) => containsText(option, searchText)),
    [searchText, countries]
  );

  const displayedAccountManager = useMemo(
    () =>
    employees.length > 0 &&
    employees?.filter((option) => containsText(option.name, searchText)),
    [searchText, employees]
  );

  return (
    <Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} sx={{ pt: 3, pb: 1, pl: 2 }}>
          <Typography style={{ fontSize: '15px' }} variant="h3">
            Edit Client
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Client Name<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="editCustomerName"
              inputProps={{ maxLength: 30 }}
              size="small"
              {...register('name', {})}
              style={TextFieldStyle}
              fullWidth
              placeholder="Client Name"
              variant="outlined"
              value={customerObj.name}
              onChange={(e) => {
                if (e.target.value.length > 30) {
                  setNameError('Client name must not exceed 30 characters');
                } else {
                  setNameError('');
                }
                setCustomerObj((prev) => ({
                  ...prev,
                  name: e.target.value
                }));
              }}
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {nameError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Client Code
            </Typography>
            <TextField
              id="editCustomerCode"
              className="disabledTextbox"
              aria-readonly
              size="small"
              sx={{
                input: {
                  background: '#d1d1d1',
                  borderRadius: 0.5
                }
              }}
              {...register('code', {})}
              style={{
                ...TextFieldStyle,
                pointerEvents: 'none'
              }}
              fullWidth
              placeholder="Client Code"
              variant="outlined"
              value={data.code}
            />
          </Grid>

          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Country<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              id="editCountryName"
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              className="select-text"
              size="small"
              style={selectStyle}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              {...register('region', {})}
              defaultValue={customerObj?.region}
              value={customerObj?.region}
              onChange={(e) => {
                setCustomerObj((prev) => ({
                  ...prev,
                  region: e.target.value
                }));
              }}
              onClose={() => setSearchText('')}
            >
              <ListSubheader>
              <TextField
               id="searchEditCountryName"
               size="small"
               autoFocus
               placeholder="Type to search..."
               fullWidth
               InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              ></TextField>
            </ListSubheader>
             {displayedCountries.length > 0 &&
              displayedCountries?.map((option, i) => (
                <MenuItem key={i} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {regionError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Client Industry<span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              id="editCustomerIndustry"
              inputProps={{ maxLength: 30 }}
              size="small"
              {...register('industry', {})}
              style={TextFieldStyle}
              fullWidth
              value={customerObj.industry}
              placeholder="Enter Industry Name"
              variant="outlined"
              onChange={(e) => {
                if (e.target.value.length > 30) {
                  setIndustryError('Industry must not exceed 30 characters');
                } else {
                  setIndustryError('');
                }
                setCustomerObj((prev) => ({
                  ...prev,
                  industry: e.target.value
                }));
              }}
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {industryError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Engagement Lead<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              id="editengagementLeadName"
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              className="select-text"
              size="small"
              style={selectStyle}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              {...register('engagementLead', {})}
              defaultValue={customerObj?.engagementLeadId}
              value={customerObj?.engagementLeadId}
              onChange={(e) => {
                const value = e.target.value;
                const emp = employees.filter(
                  (em) => em.employeeId === value
                )[0];

                setCustomerObj((prev) => ({
                  ...prev,
                  engagementLeadId: emp.employeeId,
                  engagementLead: emp.name
                }));
              }}
              onClose={() => setSearchText('')}
            >
              <ListSubheader>
              <TextField
               id="searchEditengagementLead"
               size="small"
               autoFocus
               placeholder="Type to search..."
               fullWidth
               InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') {
                  e.stopPropagation();
                }
              }}
              ></TextField>
            </ListSubheader>
            {displayedAccountManager.length > 0 &&
              displayedAccountManager?.map((option, i) => (
                <MenuItem key={i} value={option.employeeId}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {engagementLeadError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={LabelStyle} fontWeight="light" variant="h5">
                Segment<span style={{ color: 'red' }}>*</span>
              </Typography>
            </Grid>

            <Select
              id="editCustomerSegmentName"
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              {...register('segment', {})}
              style={{
                height: '2.25rem',
                width: '94%',
                marginLeft: 15,
                marginTop: 5,
                paddingRight: 15,
                fontSize: '13px'
              }}
              displayEmpty
              defaultValue={selectedOption}
              value={selectedOption}
              onChange={(e) => {
                setSegmentError('');
                setSelectedOption(e.target.value);
                setSegment(e.target.value);
              }}
              onClose={() => setSearchText('')}
            >
              <ListSubheader>
                <TextField
                  id="searchEditSegmentName"
                  size="small"
                  // Autofocus on textfield
                  autoFocus
                  placeholder="Type to search..."
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                      // Prevents autoselecting item while typing (default Select behaviour)
                      e.stopPropagation();
                    }
                  }}
                />
              </ListSubheader>
              <MenuItem
                style={{ display: 'none' }}
                selected
                disabled
                value={''}
              >
                Select Segment
              </MenuItem>
              {displayedOptions.length > 0 &&
                displayedOptions?.map((option, i) => (
                  <MenuItem key={i} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {segmentError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Description
            </Typography>
            <TextField
              id="editCustomerDescription"
              inputProps={{ maxLength: 50 }}
              size="small"
              {...register('description', {})}
              style={TextFieldStyle}
              fullWidth
              placeholder="Enter Description"
              variant="outlined"
              value={customerObj.description}
              onChange={(e) => {
                if (e.target.value.length < 51) {
                  setDescError('');
                } else {
                  setDescError(
                    'Account description must not exceed 50 characters'
                  );
                }
                setCustomerObj((prev) => ({
                  ...prev,
                  description: e.target.value
                }));
              }}
              rows={2}
              multiline
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {descError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Status<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              id="status"
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              className="select-text"
              size="small"
              style={selectStyle}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              {...register('status', {})}
              defaultValue={''}
              value={customerObj.status || ''}
              onChange={(e) => {
                setCustomerObj((prev) => ({
                  ...prev,
                  status: e.target.value
                }));
                if(e.target.value) {
                  setStatusError('');
                }
              }}
            >
              <MenuItem value="Active">
                Active
              </MenuItem>
              <MenuItem value="Inactive">
                Inactive
              </MenuItem>
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {statusError}
            </Typography>
          </Grid>
          <Grid
            item
            sx={{
              mb: 2,
              mt: 2,
              pr: 2,
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={12}
          >
            <Button
              id="editCustomer-btn-onClose"
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => onClose()}
            >
              Cancel
            </Button>
            <Button
              id="editCustomer-btn-onSubmit"
              disabled={customerObj.name === '' || isLoading}
              className={isLoading ? 'loading' : ''}
              onClick={() => toggleConfirmation()}
              sx={{
                ml: 2,
                width: '10%',
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4'
              }}
              variant="contained"
              type="button"
            >
              Update
            </Button>
          </Grid>
        </Grid>
      </form>
      <ConfirmationModal
        isLoading={isLoading}
        open={openConfirmation}
        toggle={toggleConfirmation}
        message={`Are you sure want to update this client?`}
        onConfirm={() => onSubmit()}
        noToggle={toggleConfirmation}
      />
    </Fragment>
  );
};

export default EditCustomer;
